import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  TextField,
  Menu,
  MenuItem,
  Divider,
  LinearProgress,
  Breadcrumbs,
  Link,
  Tooltip,
  CircularProgress,
  Collapse,
  Autocomplete,
  Avatar,
  Chip,
} from '@mui/material';
import {
  Close as CloseIcon,
  CloudUpload as UploadIcon,
  CreateNewFolder as NewFolderIcon,
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  InsertDriveFile as FileIcon,
  Description as CsvIcon,
  PictureAsPdf as PdfIcon,
  Code as JsonIcon,
  Archive as ZipIcon,
  Image as ImageIcon,
  TextSnippet as TextIcon,
  Article as DocIcon,
  Download as DownloadIcon,
  Edit as RenameIcon,
  Delete as DeleteIcon,
  MoreVert as MoreIcon,
  NavigateNext as NavigateNextIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  Share as ShareIcon,
  Person as PersonIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import {
  getFileIcon,
  formatFileSize,
  formatFileDate,
  getFolderContents,
  getBreadcrumbPath,
} from '../utils/sampleResponses';

// Custom Folder Tree Item Component with lazy loading support
const FolderTreeItem = ({ folder, items, selectedFolderId, expandedFolders, onSelect, onToggle, onLoadFolder, level = 0, colors }) => {
  const isSelected = selectedFolderId === folder.id;
  const isExpanded = expandedFolders.includes(folder.id);
  const isLoading = folder.isLoading;
  const isLoaded = folder.isLoaded !== false; // Default to true for backwards compatibility
  const childFolders = items.filter(item => item.parentId === folder.id && item.type === 'FOLDER');

  // Show expand arrow if: has loaded children OR backend says hasChildren
  const showExpandArrow = childFolders.length > 0 || folder.hasChildren;

  const handleToggleClick = (e) => {
    e.stopPropagation();
    if (!showExpandArrow) return;

    // If not loaded and has children, trigger load
    if (!isLoaded && folder.hasChildren && onLoadFolder) {
      onLoadFolder(folder.id, folder.folderPath);
    }
    onToggle(folder.id);
  };

  const handleSelect = () => {
    onSelect(folder.id);
    // Also load contents when selecting if not loaded
    if (!isLoaded && folder.hasChildren && onLoadFolder) {
      onLoadFolder(folder.id, folder.folderPath);
    }
  };

  return (
    <>
      <ListItemButton
        onClick={handleSelect}
        selected={isSelected}
        disabled={isLoading}
        sx={{
          pl: 1 + level * 2,
          py: 0.5,
          borderRadius: 0,
          '&.Mui-selected': {
            bgcolor: 'rgba(34, 197, 94, 0.12)',
            '&:hover': {
              bgcolor: 'rgba(34, 197, 94, 0.18)',
            },
          },
          '&:hover': {
            bgcolor: colors.hoverBg,
          },
        }}
      >
        <IconButton
          size="small"
          onClick={handleToggleClick}
          disabled={isLoading}
          sx={{
            p: 0.25,
            mr: 0.5,
            visibility: showExpandArrow ? 'visible' : 'hidden',
            color: colors.textMuted,
          }}
        >
          {isLoading ? (
            <CircularProgress size={14} sx={{ color: '#22c55e' }} />
          ) : isExpanded ? (
            <ExpandMoreIcon fontSize="small" />
          ) : (
            <ChevronRightIcon fontSize="small" />
          )}
        </IconButton>
        <ListItemIcon sx={{ minWidth: 28 }}>
          {isExpanded && isLoaded ? (
            <FolderOpenIcon sx={{ color: '#f59e0b', fontSize: 20 }} />
          ) : (
            <FolderIcon sx={{ color: '#f59e0b', fontSize: 20 }} />
          )}
        </ListItemIcon>
        <ListItemText
          primary={folder.name}
          primaryTypographyProps={{
            sx: {
              fontSize: '0.875rem',
              color: colors.textPrimary,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            },
          }}
        />
      </ListItemButton>
      {showExpandArrow && (
        <Collapse in={isExpanded && isLoaded} timeout="auto" unmountOnExit>
          <List disablePadding>
            {childFolders.map(childFolder => (
              <FolderTreeItem
                key={childFolder.id}
                folder={childFolder}
                items={items}
                selectedFolderId={selectedFolderId}
                expandedFolders={expandedFolders}
                onSelect={onSelect}
                onToggle={onToggle}
                onLoadFolder={onLoadFolder}
                level={level + 1}
                colors={colors}
              />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
};

const DriveExplorer = ({
  open,
  onClose,
  driveData,
  colors,
  onUpload,
  onDelete,
  onRename,
  onDownload,
  onCreateFolder,
  onShare,
  onLoadFolder,
  onSearch,
  organizationUsers = [],
}) => {
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [expandedFolders, setExpandedFolders] = useState([]);
  const [contextMenu, setContextMenu] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newFolderDialogOpen, setNewFolderDialogOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null); // { done, total } while a multi-file/folder upload is in progress
  const [uploadMenuAnchor, setUploadMenuAnchor] = useState(null);
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);

  // Share dialog state
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [selectedShareUsers, setSelectedShareUsers] = useState([]);
  const [shareUserInput, setShareUserInput] = useState('');

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searchDialogOpen, setSearchDialogOpen] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);

  // Initialize with root folder
  useEffect(() => {
    if (driveData && !selectedFolderId) {
      setSelectedFolderId(driveData.rootFolderId);
      setExpandedFolders([driveData.rootFolderId]);
    }
  }, [driveData, selectedFolderId]);

  if (!driveData) return null;

  const { items, usage, title } = driveData;

  // Get root folders for tree
  const rootFolder = items.find(item => item.id === driveData.rootFolderId);

  // Get contents of current folder
  const currentFolderContents = getFolderContents(items, selectedFolderId);

  // Get breadcrumb path
  const breadcrumbPath = getBreadcrumbPath(items, selectedFolderId);

  // Get file type icon component
  const getFileIconComponent = (mimeType, isFolder) => {
    if (isFolder) return <FolderIcon sx={{ color: '#f59e0b' }} />;

    const iconType = getFileIcon(mimeType);
    const iconMap = {
      csv: <CsvIcon sx={{ color: '#22c55e' }} />,
      pdf: <PdfIcon sx={{ color: '#ef4444' }} />,
      json: <JsonIcon sx={{ color: '#f59e0b' }} />,
      zip: <ZipIcon sx={{ color: '#8b5cf6' }} />,
      image: <ImageIcon sx={{ color: '#3b82f6' }} />,
      text: <TextIcon sx={{ color: '#71717a' }} />,
      doc: <DocIcon sx={{ color: '#3b82f6' }} />,
      file: <FileIcon sx={{ color: '#71717a' }} />,
    };
    return iconMap[iconType] || iconMap.file;
  };

  // Handle folder selection in tree
  const handleFolderSelect = (folderId) => {
    setSelectedFolderId(folderId);
    // Auto-expand when selecting
    if (!expandedFolders.includes(folderId)) {
      setExpandedFolders([...expandedFolders, folderId]);
    }
  };

  // Handle expand/collapse toggle
  const handleToggle = (folderId) => {
    setExpandedFolders(prev =>
      prev.includes(folderId)
        ? prev.filter(id => id !== folderId)
        : [...prev, folderId]
    );
  };

  // Handle item click in file list
  const handleItemClick = (item) => {
    if (item.type === 'FOLDER') {
      setSelectedFolderId(item.id);
      if (!expandedFolders.includes(item.id)) {
        setExpandedFolders([...expandedFolders, item.id]);
      }
      // Trigger lazy load if folder not loaded
      if (item.isLoaded === false && item.hasChildren && onLoadFolder) {
        onLoadFolder(item.id, item.folderPath);
      }
    }
  };

  // Handle context menu
  const handleContextMenu = (event, item) => {
    event.preventDefault();
    event.stopPropagation();
    setContextMenu({ mouseX: event.clientX, mouseY: event.clientY });
    setSelectedItem(item);
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  // Handle download
  const handleDownloadClick = (item) => {
    if (onDownload) {
      onDownload(item);
    }
    handleCloseContextMenu();
  };

  // Handle rename
  const handleRenameClick = () => {
    if (selectedItem) {
      setNewName(selectedItem.name);
      setRenameDialogOpen(true);
    }
    handleCloseContextMenu();
  };

  const handleRenameConfirm = () => {
    if (selectedItem && newName.trim() && onRename) {
      onRename(selectedItem.id, newName.trim());
    }
    setRenameDialogOpen(false);
    setNewName('');
    setSelectedItem(null);
  };

  // Handle delete
  const handleDeleteClick = () => {
    if (selectedItem && onDelete) {
      onDelete(selectedItem.id);
    }
    handleCloseContextMenu();
    setSelectedItem(null);
  };

  // Handle share
  const handleShareClick = () => {
    setShareDialogOpen(true);
    handleCloseContextMenu();
  };

  const handleShareConfirm = () => {
    if (selectedItem && selectedShareUsers.length > 0 && onShare) {
      onShare(selectedItem, selectedShareUsers);
    }
    setShareDialogOpen(false);
    setSelectedShareUsers([]);
    setShareUserInput('');
    setSelectedItem(null);
  };

  const handleShareDialogClose = () => {
    setShareDialogOpen(false);
    setSelectedShareUsers([]);
    setShareUserInput('');
  };

  const handleAddShareUser = (user) => {
    if (user && !selectedShareUsers.find(u => u.id === user.id)) {
      setSelectedShareUsers([...selectedShareUsers, user]);
    }
  };

  const handleRemoveShareUser = (userId) => {
    setSelectedShareUsers(selectedShareUsers.filter(u => u.id !== userId));
  };

  // Handle new folder
  const handleNewFolderClick = () => {
    setNewFolderName('');
    setNewFolderDialogOpen(true);
  };

  const handleNewFolderConfirm = () => {
    if (newFolderName.trim() && onCreateFolder) {
      onCreateFolder(selectedFolderId, newFolderName.trim());
    }
    setNewFolderDialogOpen(false);
    setNewFolderName('');
  };

  // Handle file upload
  const handleUploadMenuOpen = (event) => {
    setUploadMenuAnchor(event.currentTarget);
  };

  const handleUploadMenuClose = () => {
    setUploadMenuAnchor(null);
  };

  const handleUploadFilesClick = () => {
    handleUploadMenuClose();
    fileInputRef.current?.click();
  };

  const handleUploadFolderClick = () => {
    handleUploadMenuClose();
    folderInputRef.current?.click();
  };

  const handleFileSelect = async (event) => {
    const fileList = Array.from(event.target.files || []).filter(f => !f.name.startsWith('.'));
    if (fileList.length > 0 && onUpload) {
      // webkitRelativePath (only set on a folder pick) preserves the folder/subfolder
      // structure the user picked, e.g. "MyDocs/policies/handbook.pdf" -- everything but
      // the filename becomes the extra path appended under the currently selected folder.
      const fileEntries = fileList.map(file => {
        const relPath = file.webkitRelativePath || '';
        const relativeDir = relPath ? relPath.split('/').slice(0, -1).join('/') : '';
        return { file, relativeDir };
      });

      setIsUploading(true);
      setUploadProgress({ done: 0, total: fileEntries.length });
      try {
        await onUpload(fileEntries, selectedFolderId, (done, total) => setUploadProgress({ done, total }));
      } finally {
        setIsUploading(false);
        setUploadProgress(null);
      }
    }
    // Reset input so the same file/folder can be selected again
    if (event.target) {
      event.target.value = '';
    }
  };

  // Handle breadcrumb navigation
  const handleBreadcrumbClick = (folderId) => {
    setSelectedFolderId(folderId);
    if (!expandedFolders.includes(folderId)) {
      setExpandedFolders([...expandedFolders, folderId]);
    }
  };

  // Handle search
  const handleSearch = async () => {
    if (!searchQuery.trim() || !onSearch) return;

    setIsSearching(true);
    setSearchError(null);
    setSearchDialogOpen(true);

    try {
      const results = await onSearch(searchQuery.trim());
      setSearchResults(results);
    } catch (error) {
      console.error('Search error:', error);
      setSearchError(error.message || 'Failed to search documents');
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const handleCloseSearchDialog = () => {
    setSearchDialogOpen(false);
    setSearchResults(null);
    setSearchError(null);
  };

  // Format search results as markdown
  const formatSearchResultsAsMarkdown = (results) => {
    if (!results || !results.results || results.results.length === 0) {
      return '**No results found.**\n\nTry a different search query.';
    }

    let markdown = `## Search Results\n\n`;
    markdown += `Found **${results.results.length}** matching chunks in your documents.\n\n`;
    markdown += `---\n\n`;

    results.results.forEach((result, index) => {
      const { text, score, metadata } = result;
      const relevancePercent = Math.round(score * 100);

      markdown += `### ${index + 1}. ${metadata.title || 'Untitled Document'}\n\n`;
      markdown += `**Relevance:** ${relevancePercent}% | **Type:** ${metadata.doc_type || 'DOCUMENT'}\n\n`;
      markdown += `> ${text.trim()}\n\n`;
      if (index < results.results.length - 1) {
        markdown += `---\n\n`;
      }
    });

    if (results.query_time_ms) {
      markdown += `\n\n*Search completed in ${results.query_time_ms.toFixed(0)}ms*`;
    }

    return markdown;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: colors.background,
          borderRadius: 0,
          height: '80vh',
          maxHeight: '800px',
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          bgcolor: colors.surface,
          borderBottom: `1px solid ${colors.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 1.5,
          px: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FolderIcon sx={{ color: '#22c55e', fontSize: 28 }} />
          <Typography variant="h6" sx={{ color: colors.textPrimary, fontWeight: 600 }}>
            {title}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Search box */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <TextField
              size="small"
              placeholder="Search documents..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={handleSearchKeyPress}
              sx={{
                width: 200,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 0,
                  bgcolor: colors.surfaceElevated,
                  '& fieldset': { borderColor: colors.border },
                  '&:hover fieldset': { borderColor: colors.textSecondary },
                  '&.Mui-focused fieldset': { borderColor: '#22c55e' },
                },
                '& .MuiInputBase-input': {
                  color: colors.textPrimary,
                  fontSize: '0.875rem',
                  py: 0.75,
                },
              }}
            />
            <IconButton
              onClick={handleSearch}
              disabled={!searchQuery.trim() || isSearching}
              sx={{
                color: searchQuery.trim() ? '#22c55e' : colors.textSecondary,
                '&:hover': { bgcolor: 'rgba(34, 197, 94, 0.08)' },
              }}
            >
              {isSearching ? <CircularProgress size={20} /> : <SearchIcon />}
            </IconButton>
          </Box>
          {/* Usage indicator */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 2 }}>
            <Typography variant="body2" sx={{ color: colors.textSecondary }}>
              {usage.used} / {usage.total}
            </Typography>
            <Box sx={{ width: 100 }}>
              <LinearProgress
                variant="determinate"
                value={usage.percentage}
                sx={{
                  height: 6,
                  borderRadius: 0,
                  bgcolor: colors.surfaceElevated,
                  '& .MuiLinearProgress-bar': {
                    bgcolor: '#22c55e',
                    borderRadius: 0,
                  },
                }}
              />
            </Box>
          </Box>
          {/* Upload button */}
          <Button
            variant="outlined"
            startIcon={isUploading ? <CircularProgress size={16} /> : <UploadIcon />}
            onClick={handleUploadMenuOpen}
            disabled={isUploading}
            sx={{
              borderColor: '#22c55e',
              color: '#22c55e',
              borderRadius: 0,
              '&:hover': {
                borderColor: '#16a34a',
                bgcolor: 'rgba(34, 197, 94, 0.08)',
              },
            }}
          >
            {isUploading && uploadProgress
              ? `Uploading ${uploadProgress.done}/${uploadProgress.total}`
              : 'Upload'}
          </Button>
          <Menu
            anchorEl={uploadMenuAnchor}
            open={Boolean(uploadMenuAnchor)}
            onClose={handleUploadMenuClose}
            PaperProps={{ sx: { bgcolor: colors.surface, borderRadius: 0, border: `1px solid ${colors.border}` } }}
          >
            <MenuItem onClick={handleUploadFilesClick}>
              <ListItemIcon><UploadIcon fontSize="small" sx={{ color: '#22c55e' }} /></ListItemIcon>
              <ListItemText primary="Upload Files" secondary="Select one or more files" />
            </MenuItem>
            <MenuItem onClick={handleUploadFolderClick}>
              <ListItemIcon><FolderIcon fontSize="small" sx={{ color: '#22c55e' }} /></ListItemIcon>
              <ListItemText primary="Upload Folder" secondary="Select a folder — subfolders included" />
            </MenuItem>
          </Menu>
          {/* Multi-file select */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            multiple
          />
          {/* Folder select (recurses subfolders) -- webkitdirectory is non-standard but
              supported in all Chromium/Edge/Firefox-based browsers, which covers this app's
              actual deployment targets; falls back to a normal file picker elsewhere. */}
          <input
            type="file"
            ref={folderInputRef}
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            webkitdirectory=""
            directory=""
            multiple
          />
          {/* Close button */}
          <IconButton
            onClick={onClose}
            sx={{ color: colors.textSecondary }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Breadcrumb navigation */}
        <Box
          sx={{
            px: 2,
            py: 1,
            bgcolor: colors.surface,
            borderBottom: `1px solid ${colors.border}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" sx={{ color: colors.textMuted }} />}
            sx={{ '& .MuiBreadcrumbs-li': { display: 'flex', alignItems: 'center' } }}
          >
            {breadcrumbPath.map((item, index) => (
              <Link
                key={item.id}
                component="button"
                variant="body2"
                underline="hover"
                onClick={() => handleBreadcrumbClick(item.id)}
                sx={{
                  color: index === breadcrumbPath.length - 1 ? colors.textPrimary : colors.textSecondary,
                  fontWeight: index === breadcrumbPath.length - 1 ? 500 : 400,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                {index === 0 && <FolderIcon sx={{ fontSize: 16, color: '#f59e0b' }} />}
                {item.name}
              </Link>
            ))}
          </Breadcrumbs>
          <Button
            size="small"
            startIcon={<NewFolderIcon />}
            onClick={handleNewFolderClick}
            sx={{
              color: colors.textSecondary,
              '&:hover': {
                color: '#22c55e',
                bgcolor: 'rgba(34, 197, 94, 0.08)',
              },
            }}
          >
            New Folder
          </Button>
        </Box>

        {/* Main content area - split view */}
        <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
          {/* Folder tree (left panel) */}
          <Box
            sx={{
              width: '30%',
              minWidth: 200,
              maxWidth: 300,
              borderRight: `1px solid ${colors.border}`,
              overflow: 'auto',
              bgcolor: colors.surface,
              py: 1,
            }}
          >
            <List disablePadding>
              {rootFolder && (
                <FolderTreeItem
                  folder={rootFolder}
                  items={items}
                  selectedFolderId={selectedFolderId}
                  expandedFolders={expandedFolders}
                  onSelect={handleFolderSelect}
                  onToggle={handleToggle}
                  onLoadFolder={onLoadFolder}
                  colors={colors}
                />
              )}
            </List>
          </Box>

          {/* File list (right panel) */}
          <Box sx={{ flexGrow: 1, overflow: 'auto', bgcolor: colors.background }}>
            {/* Column headers */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                px: 2,
                py: 1,
                borderBottom: `1px solid ${colors.border}`,
                bgcolor: colors.surface,
              }}
            >
              <Typography variant="caption" sx={{ color: colors.textMuted, flex: 2, fontWeight: 600 }}>
                Name
              </Typography>
              <Typography variant="caption" sx={{ color: colors.textMuted, width: 100, fontWeight: 600 }}>
                Size
              </Typography>
              <Typography variant="caption" sx={{ color: colors.textMuted, width: 120, fontWeight: 600 }}>
                Modified
              </Typography>
              <Box sx={{ width: 100 }} />
            </Box>

            {/* File/Folder list */}
            <List sx={{ py: 0 }}>
              {currentFolderContents.length === 0 ? (
                <ListItem>
                  <ListItemText
                    primary="This folder is empty"
                    primaryTypographyProps={{
                      sx: { color: colors.textMuted, textAlign: 'center', py: 4 },
                    }}
                  />
                </ListItem>
              ) : (
                currentFolderContents
                  .sort((a, b) => {
                    // Folders first, then files
                    if (a.type !== b.type) return a.type === 'FOLDER' ? -1 : 1;
                    return a.name.localeCompare(b.name);
                  })
                  .map((item) => (
                    <ListItem
                      key={item.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        px: 2,
                        py: 1,
                        cursor: item.type === 'FOLDER' ? 'pointer' : 'default',
                        opacity: item.isLoading ? 0.6 : 1,
                        '&:hover': {
                          bgcolor: colors.hoverBg,
                        },
                      }}
                      onClick={() => !item.isLoading && handleItemClick(item)}
                      onContextMenu={(e) => handleContextMenu(e, item)}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', flex: 2, gap: 1.5 }}>
                        <ListItemIcon sx={{ minWidth: 'auto' }}>
                          {item.isLoading ? (
                            <CircularProgress size={20} sx={{ color: '#22c55e' }} />
                          ) : (
                            getFileIconComponent(item.mimeType, item.type === 'FOLDER')
                          )}
                        </ListItemIcon>
                        <ListItemText
                          primary={item.name}
                          primaryTypographyProps={{
                            sx: {
                              color: colors.textPrimary,
                              fontSize: '0.875rem',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            },
                          }}
                        />
                        {item.type === 'FOLDER' && item.isLoaded === false && !item.isLoading && (
                          <Typography variant="caption" sx={{ color: colors.textMuted, ml: 1 }}>
                            Click to load
                          </Typography>
                        )}
                      </Box>
                      <Typography variant="body2" sx={{ color: colors.textSecondary, width: 100 }}>
                        {item.type === 'FOLDER' ? '-' : formatFileSize(item.size)}
                      </Typography>
                      <Typography variant="body2" sx={{ color: colors.textSecondary, width: 120 }}>
                        {formatFileDate(item.modifiedAt)}
                      </Typography>
                      <Box sx={{ width: 100, display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                        {item.type === 'FILE' && (
                          <Tooltip title="Download">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDownloadClick(item);
                              }}
                              sx={{ color: colors.textMuted, '&:hover': { color: '#22c55e' } }}
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="More actions">
                          <IconButton
                            size="small"
                            onClick={(e) => handleContextMenu(e, item)}
                            sx={{ color: colors.textMuted, '&:hover': { color: colors.textPrimary } }}
                          >
                            <MoreIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </ListItem>
                  ))
              )}
            </List>
          </Box>
        </Box>
      </DialogContent>

      {/* Context Menu */}
      <Menu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
        PaperProps={{
          sx: {
            bgcolor: colors.surface,
            borderRadius: 0,
            border: `1px solid ${colors.border}`,
          },
        }}
      >
        {selectedItem?.type === 'FILE' && (
          <MenuItem onClick={() => handleDownloadClick(selectedItem)}>
            <ListItemIcon>
              <DownloadIcon fontSize="small" sx={{ color: colors.textSecondary }} />
            </ListItemIcon>
            <ListItemText primary="Download" primaryTypographyProps={{ sx: { color: colors.textPrimary } }} />
          </MenuItem>
        )}
        <MenuItem onClick={handleRenameClick}>
          <ListItemIcon>
            <RenameIcon fontSize="small" sx={{ color: colors.textSecondary }} />
          </ListItemIcon>
          <ListItemText primary="Rename" primaryTypographyProps={{ sx: { color: colors.textPrimary } }} />
        </MenuItem>
        <MenuItem onClick={handleShareClick}>
          <ListItemIcon>
            <ShareIcon fontSize="small" sx={{ color: colors.textSecondary }} />
          </ListItemIcon>
          <ListItemText primary="Share" primaryTypographyProps={{ sx: { color: colors.textPrimary } }} />
        </MenuItem>
        <Divider sx={{ borderColor: colors.border }} />
        <MenuItem onClick={handleDeleteClick}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" sx={{ color: '#ef4444' }} />
          </ListItemIcon>
          <ListItemText primary="Delete" primaryTypographyProps={{ sx: { color: '#ef4444' } }} />
        </MenuItem>
      </Menu>

      {/* Rename Dialog */}
      <Dialog
        open={renameDialogOpen}
        onClose={() => setRenameDialogOpen(false)}
        PaperProps={{
          sx: {
            bgcolor: colors.background,
            borderRadius: 0,
          },
        }}
      >
        <DialogTitle sx={{ color: colors.textPrimary }}>Rename</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleRenameConfirm()}
            sx={{
              mt: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: 0,
                bgcolor: colors.surface,
                '& fieldset': { borderColor: colors.border },
                '&:hover fieldset': { borderColor: '#22c55e' },
                '&.Mui-focused fieldset': { borderColor: '#22c55e' },
              },
              '& .MuiInputBase-input': { color: colors.textPrimary },
            }}
          />
        </DialogContent>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, p: 2, pt: 0 }}>
          <Button onClick={() => setRenameDialogOpen(false)} sx={{ color: colors.textSecondary }}>
            Cancel
          </Button>
          <Button
            onClick={handleRenameConfirm}
            variant="contained"
            sx={{
              bgcolor: '#22c55e',
              borderRadius: 0,
              '&:hover': { bgcolor: '#16a34a' },
            }}
          >
            Rename
          </Button>
        </Box>
      </Dialog>

      {/* New Folder Dialog */}
      <Dialog
        open={newFolderDialogOpen}
        onClose={() => setNewFolderDialogOpen(false)}
        PaperProps={{
          sx: {
            bgcolor: colors.background,
            borderRadius: 0,
          },
        }}
      >
        <DialogTitle sx={{ color: colors.textPrimary }}>New Folder</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            placeholder="Folder name"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleNewFolderConfirm()}
            sx={{
              mt: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: 0,
                bgcolor: colors.surface,
                '& fieldset': { borderColor: colors.border },
                '&:hover fieldset': { borderColor: '#22c55e' },
                '&.Mui-focused fieldset': { borderColor: '#22c55e' },
              },
              '& .MuiInputBase-input': { color: colors.textPrimary },
            }}
          />
        </DialogContent>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, p: 2, pt: 0 }}>
          <Button onClick={() => setNewFolderDialogOpen(false)} sx={{ color: colors.textSecondary }}>
            Cancel
          </Button>
          <Button
            onClick={handleNewFolderConfirm}
            variant="contained"
            disabled={!newFolderName.trim()}
            sx={{
              bgcolor: '#22c55e',
              borderRadius: 0,
              '&:hover': { bgcolor: '#16a34a' },
            }}
          >
            Create
          </Button>
        </Box>
      </Dialog>

      {/* Share Dialog */}
      <Dialog
        open={shareDialogOpen}
        onClose={handleShareDialogClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: colors.background,
            borderRadius: 0,
          },
        }}
      >
        <DialogTitle sx={{ color: colors.textPrimary, display: 'flex', alignItems: 'center', gap: 1 }}>
          <ShareIcon sx={{ color: '#22c55e' }} />
          Share "{selectedItem?.name}"
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 2 }}>
            Grant view access to a team member in your organization.
          </Typography>

          {/* Selected item info */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: 1.5,
            mb: 2,
            bgcolor: colors.surface,
            border: `1px solid ${colors.border}`,
          }}>
            {selectedItem?.type === 'FOLDER' ? (
              <FolderIcon sx={{ color: '#f59e0b' }} />
            ) : (
              <FileIcon sx={{ color: colors.textMuted }} />
            )}
            <Box>
              <Typography variant="body2" sx={{ color: colors.textPrimary, fontWeight: 500 }}>
                {selectedItem?.name}
              </Typography>
              <Typography variant="caption" sx={{ color: colors.textMuted }}>
                {selectedItem?.type === 'FOLDER' ? 'Folder' : 'File'} • View access
              </Typography>
            </Box>
          </Box>

          {/* User selector */}
          <Autocomplete
            options={organizationUsers.filter(u => !selectedShareUsers.find(s => s.id === u.id))}
            getOptionLabel={(option) => option.displayName || option.username || option.email || ''}
            value={null}
            onChange={(event, newValue) => {
              if (newValue) {
                handleAddShareUser(newValue);
                setShareUserInput('');
              }
            }}
            inputValue={shareUserInput}
            onInputChange={(event, newInputValue, reason) => {
              if (reason !== 'reset') {
                setShareUserInput(newInputValue);
              }
            }}
            blurOnSelect
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            renderOption={(props, option) => (
              <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    bgcolor: '#22c55e',
                    fontSize: '0.875rem',
                  }}
                >
                  {(option.displayName || option.username || 'U')[0].toUpperCase()}
                </Avatar>
                <Box>
                  <Typography variant="body2" sx={{ color: colors.textPrimary }}>
                    {option.displayName || option.username}
                  </Typography>
                  {option.email && (
                    <Typography variant="caption" sx={{ color: colors.textMuted }}>
                      {option.email}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search for team members to add..."
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 0,
                    bgcolor: colors.surface,
                    '& fieldset': { borderColor: colors.border },
                    '&:hover fieldset': { borderColor: '#22c55e' },
                    '&.Mui-focused fieldset': { borderColor: '#22c55e' },
                  },
                  '& .MuiInputBase-input': { color: colors.textPrimary },
                }}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <PersonIcon sx={{ color: colors.textMuted, mr: 1 }} />
                      {params.InputProps.startAdornment}
                    </>
                  ),
                }}
              />
            )}
            noOptionsText={
              <Typography sx={{ color: colors.textMuted, py: 1 }}>
                {selectedShareUsers.length === organizationUsers.length
                  ? 'All team members added'
                  : 'No team members found'}
              </Typography>
            }
            ListboxProps={{
              sx: {
                bgcolor: colors.surface,
                '& .MuiAutocomplete-option': {
                  '&:hover': { bgcolor: colors.hoverBg },
                  '&[aria-selected="true"]': { bgcolor: 'rgba(34, 197, 94, 0.12)' },
                },
              },
            }}
            PaperComponent={({ children, ...props }) => (
              <Box
                {...props}
                sx={{
                  bgcolor: colors.surface,
                  border: `1px solid ${colors.border}`,
                  borderRadius: 0,
                  mt: 0.5,
                }}
              >
                {children}
              </Box>
            )}
          />

          {/* Selected users list */}
          {selectedShareUsers.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" sx={{ color: colors.textMuted, mb: 1, display: 'block' }}>
                Sharing with ({selectedShareUsers.length}):
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {selectedShareUsers.map((user) => (
                  <Chip
                    key={user.id}
                    avatar={
                      <Avatar sx={{ bgcolor: '#22c55e' }}>
                        {(user.displayName || user.username || 'U')[0].toUpperCase()}
                      </Avatar>
                    }
                    label={user.displayName || user.username}
                    onDelete={() => handleRemoveShareUser(user.id)}
                    sx={{
                      bgcolor: colors.surface,
                      border: `1px solid ${colors.border}`,
                      borderRadius: 0,
                      '& .MuiChip-label': { color: colors.textPrimary },
                      '& .MuiChip-deleteIcon': { color: colors.textMuted, '&:hover': { color: '#ef4444' } },
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, p: 2, pt: 0 }}>
          <Button onClick={handleShareDialogClose} sx={{ color: colors.textSecondary }}>
            Cancel
          </Button>
          <Button
            onClick={handleShareConfirm}
            variant="contained"
            disabled={selectedShareUsers.length === 0}
            startIcon={<ShareIcon />}
            sx={{
              bgcolor: '#22c55e',
              borderRadius: 0,
              '&:hover': { bgcolor: '#16a34a' },
              '&.Mui-disabled': {
                bgcolor: colors.surfaceElevated,
                color: colors.textMuted,
              },
            }}
          >
            Share
          </Button>
        </Box>
      </Dialog>

      {/* Search Results Dialog */}
      <Dialog
        open={searchDialogOpen}
        onClose={handleCloseSearchDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: colors.background,
            borderRadius: 0,
            maxHeight: '80vh',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            bgcolor: colors.surface,
            borderBottom: `1px solid ${colors.border}`,
            py: 1.5,
            px: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SearchIcon sx={{ color: '#22c55e' }} />
            <Typography variant="h6" sx={{ color: colors.textPrimary, fontWeight: 600 }}>
              Search Results
            </Typography>
            {searchQuery && (
              <Typography variant="body2" sx={{ color: colors.textSecondary, ml: 1 }}>
                for "{searchQuery}"
              </Typography>
            )}
          </Box>
          <IconButton onClick={handleCloseSearchDialog} sx={{ color: colors.textSecondary }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            p: 3,
            bgcolor: colors.background,
            '& h2': { color: colors.textPrimary, fontSize: '1.25rem', mt: 0 },
            '& h3': { color: colors.textPrimary, fontSize: '1rem', mb: 0.5 },
            '& p': { color: colors.textSecondary, lineHeight: 1.6 },
            '& blockquote': {
              borderLeft: `3px solid #22c55e`,
              pl: 2,
              ml: 0,
              my: 1,
              color: colors.textPrimary,
              bgcolor: colors.surfaceElevated,
              py: 1,
              pr: 2,
            },
            '& hr': { borderColor: colors.border, my: 2 },
            '& strong': { color: colors.textPrimary },
            '& em': { color: colors.textMuted },
          }}
        >
          {isSearching ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
              <CircularProgress sx={{ color: '#22c55e', mb: 2 }} />
              <Typography sx={{ color: colors.textSecondary }}>
                Searching your documents...
              </Typography>
            </Box>
          ) : searchError ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography sx={{ color: '#ef4444', mb: 1 }}>
                Search Failed
              </Typography>
              <Typography sx={{ color: colors.textSecondary }}>
                {searchError}
              </Typography>
            </Box>
          ) : (
            <ReactMarkdown>
              {formatSearchResultsAsMarkdown(searchResults)}
            </ReactMarkdown>
          )}
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default DriveExplorer;
